import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import {
  faInstagram,
  faSpotify,
  faTiktok,
  faTwitter,
} from '@soundxyz/font-awesome/free-brands-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { CopyAddress } from '@components/CopyAddress';
import { LoadingBars } from '@components/LoadingBars/LoadingBars';
import { Avatar } from '@components/basics/Images/Avatar';
import { LoadingSkeleton } from '@components/basics/LoadingSkeleton';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { useAuth } from '@context/auth/base';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, UserInfoSectionFragmentDoc } from '@graphql/reactQuery';
import { useUpdateAvatar } from '@hooks/useUpdateAvatar';
import { sleep } from '@utils/helpers';
import { ArtistActionBar } from '../ActionBar/ArtistActionBar';
import { CollectorActionBar } from '../ActionBar/CollectorActionBar';
import { ProfileImageEditButton } from '../Edit/ProfileImageEditButton';
import { UserStats } from './UserStats';
import { CollectorBadge } from './components/CollectorBadge';
import { InvitedBy } from './components/InvitedBy';
import { SocialProof } from './components/SocialProof';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UserInfoSection on User {
    id
    username
    instagramHandle
    twitterHandle
    tiktokUrl
    publicAddress
    webappUri
    followerCount
    invitedByUser {
      ...InvitedUserBy
    }
    artist {
      id
      name
      spotifyUrl
    }
    topCollectorPosition
    avatar {
      id
      url
    }
    ...CollectorActionBar
    ...UserStats
    ...ArtistActionBar
  }

  fragment InvitedUserBy on User {
    id
    username
    publicAddress
    webappUri
    avatar {
      id
      url
    }
  }
`);

const MOBILE_AVATAR_SIZE = 108;
const TABLET_AVATAR_SIZE = 144;

export const UserInfo = ({
  userData,
  loading,
}: {
  userData: FragmentType<UserInfoSectionFragmentDoc>;
  loading?: boolean;
}) => {
  const userInfo = getFragment(UserInfoSectionFragmentDoc, userData);
  const { authUser } = useAuth();
  const {
    avatar,
    publicAddress,
    webappUri,
    topCollectorPosition,
    username,
    artist,
    followerCount,
    instagramHandle,
    twitterHandle,
    tiktokUrl,
    invitedByUser,
    id: userId,
  } = userInfo;

  const isCurrentUser = authUser?.id === userId;
  const showCollectorBadge = !artist && !!topCollectorPosition;
  const actionBar = artist ? (
    <ArtistActionBar actionBarData={userInfo} />
  ) : (
    <CollectorActionBar actionBarData={userInfo} />
  );

  const artistSpotifyUrl = artist?.spotifyUrl;

  const showSocials = !loading && (!!artistSpotifyUrl || !!twitterHandle || !!instagramHandle);
  const [updatePending, setUpdatePending] = React.useState(false);
  const { selectImage } = useUpdateAvatar({
    onConfirm: () => {
      setUpdatePending(true);
    },
    onDone: async () => {
      // perceptible loading state
      await sleep(400);
      setUpdatePending(false);
    },
  });

  // with ability for user to edit own avatar, we want to invalidate / sync on authUser rather than profile user if current user
  const avatarUrl = isCurrentUser ? authUser.avatar?.url : avatar?.url;

  return (
    <div className="flex w-full flex-col gap-6 px-5 lt:px-0">
      <div className="relative flex w-full items-end justify-between lg:items-end">
        <div className="flex md:hidden">
          <LoadingSkeleton loading={!!loading} className="h-[108px] w-[108px]">
            <Avatar
              size={MOBILE_AVATAR_SIZE}
              src={avatarUrl}
              walletAddress={publicAddress}
              borderRadius={8}
              hasShadow
              disableLink
              webappUri={webappUri}
            />
          </LoadingSkeleton>
        </div>
        <div className="group z-above2 hidden md:flex">
          <LoadingSkeleton loading={!!loading}>
            <Avatar
              size={TABLET_AVATAR_SIZE}
              src={avatarUrl}
              walletAddress={publicAddress}
              borderRadius={8}
              hasShadow
              disableLink
              webappUri={webappUri}
            >
              <div
                className={twMerge(
                  'pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden rounded-md bg-black/70 transition-opacity duration-200',
                  updatePending ? 'opacity-100' : 'opacity-0',
                )}
              >
                <LoadingBars height={undefined} barClassName="bg-white" className="m-0 h-12" />
              </div>
              {isCurrentUser && (
                <ProfileImageEditButton onClick={selectImage} className="absolute right-2 top-2" />
              )}
            </Avatar>
          </LoadingSkeleton>
        </div>
        {showCollectorBadge && (
          <div className="absolute -bottom-3 right-0 hidden lg:block">
            <CollectorBadge rank={topCollectorPosition} />
          </div>
        )}
        <div className="block lg:hidden">{actionBar}</div>
      </div>
      <div className="relative flex w-full flex-col gap-4">
        <div className="flex w-full flex-col gap-1 md:gap-2">
          <div className="flex w-full items-center justify-between">
            <LoadingSkeleton
              loading={!!loading}
              className="mb-[6px] h-5 w-2/5 md:mb-[10px] md:h-6 md:w-1/4"
            >
              <div className="flex items-center gap-1">
                <h2 className="font-title text-title-l font-medium md:text-title-xl">{username}</h2>
                {artist && (
                  <>
                    <div className="block md:hidden">
                      <VerifiedTooltip />
                    </div>
                    <div className="hidden md:block">
                      <VerifiedTooltip size={22} />
                    </div>
                  </>
                )}
              </div>
            </LoadingSkeleton>
            <div className="hidden lg:block">{actionBar}</div>
          </div>
          <div className="flex gap-4 font-base text-base-m font-medium text-base400 md:gap-6">
            <CopyAddress
              publicAddress={publicAddress}
              showIcon
              className="font-base text-base-m font-medium text-base400"
              loading={loading}
            />
            {showSocials && (
              <div className="flex items-center gap-2 text-title-m md:gap-[18px]">
                {!!artistSpotifyUrl && (
                  <AccessibleLink absoluteUri={artistSpotifyUrl} type="external">
                    <IconButton
                      className="text-base400"
                      size={20}
                      icon={<FontAwesomeIcon icon={faSpotify} />}
                    />
                  </AccessibleLink>
                )}
                {!!twitterHandle && (
                  <AccessibleLink
                    absoluteUri={`https://twitter.com/${twitterHandle}`}
                    type="external"
                  >
                    <IconButton
                      className="text-base400"
                      size={20}
                      icon={<FontAwesomeIcon icon={faTwitter} />}
                    />
                  </AccessibleLink>
                )}
                {!!instagramHandle && (
                  <AccessibleLink
                    absoluteUri={`https://instagram.com/${instagramHandle}`}
                    type="external"
                  >
                    <IconButton
                      className="text-base400"
                      size={20}
                      icon={<FontAwesomeIcon icon={faInstagram} />}
                    />
                  </AccessibleLink>
                )}
                {!!tiktokUrl && (
                  <AccessibleLink absoluteUri={tiktokUrl} type="external">
                    <IconButton
                      className="text-base400"
                      size={20}
                      icon={<FontAwesomeIcon icon={faTiktok} />}
                    />
                  </AccessibleLink>
                )}
              </div>
            )}
          </div>
          <InvitedBy user={invitedByUser} showSelectUser={isCurrentUser && !invitedByUser} />
        </div>
        {showCollectorBadge && (
          <div className="absolute right-0 block lg:hidden">
            <CollectorBadge rank={topCollectorPosition} />
          </div>
        )}
        <UserStats statsData={userInfo} loading={loading} />
        {followerCount !== 0 && <SocialProof userId={userInfo.id} loading={loading} />}
      </div>
    </div>
  );
};
